const SAML_MD_NS = 'urn:oasis:names:tc:SAML:2.0:metadata';
const XML_DSIG_NS = 'http://www.w3.org/2000/09/xmldsig#';
const SSO_BINDINGS = [
  'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST',
  'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect',
];
// Real IdP metadata is a few KB; anything far larger is not a metadata file.
export const MAX_METADATA_XML_LENGTH = 100_000;

export const SAML_METADATA_INVALID_MSG =
  "This file doesn't look like a valid SAML metadata document.";
export const SAML_METADATA_TOO_LARGE_MSG = 'The metadata file is too large.';
export const SAML_METADATA_NO_LOGIN_URL_MSG =
  'The metadata has no Login URL (SingleSignOnService with an HTTP-POST or HTTP-Redirect binding).';
export const SAML_METADATA_NO_CERTIFICATE_MSG = 'The metadata has no signing certificate.';
export const SAML_METADATA_MULTIPLE_CERTIFICATES_MSG =
  'The metadata has more than one signing certificate; enter the active one manually.';

export interface SamlMetadataValues {
  samlIssuerId: string;
  loginUrl: string;
  certificate: string;
}

// Mirrors parse_saml_metadata in INE-business-api, which re-parses the file on save
// and stays the source of truth. Returns the values or an error message.
export const parseSamlMetadata = (
  xml: string
): { values: SamlMetadataValues } | { error: string } => {
  if (xml.length > MAX_METADATA_XML_LENGTH) return { error: SAML_METADATA_TOO_LARGE_MSG };
  if (/<!(DOCTYPE|ENTITY)/i.test(xml)) return { error: SAML_METADATA_INVALID_MSG };

  const doc = new DOMParser().parseFromString(xml.replace(/^\uFEFF/, '').trim(), 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) return { error: SAML_METADATA_INVALID_MSG };

  const root = doc.documentElement;
  const entity =
    root.namespaceURI === SAML_MD_NS && root.localName === 'EntityDescriptor'
      ? root
      : doc.getElementsByTagNameNS(SAML_MD_NS, 'EntityDescriptor')[0];
  const idp = entity?.getElementsByTagNameNS(SAML_MD_NS, 'IDPSSODescriptor')[0];
  const samlIssuerId = entity?.getAttribute('entityID');
  if (!idp || !samlIssuerId) return { error: SAML_METADATA_INVALID_MSG };

  // Look only inside IDPSSODescriptor, never at the document's own <Signature>.
  const services = Array.from(idp.getElementsByTagNameNS(SAML_MD_NS, 'SingleSignOnService'));
  const loginUrl = SSO_BINDINGS.map((binding) =>
    services
      .find(
        (service) => service.getAttribute('Binding') === binding && service.getAttribute('Location')
      )
      ?.getAttribute('Location')
  ).find(Boolean);
  if (!loginUrl) return { error: SAML_METADATA_NO_LOGIN_URL_MSG };

  const certificates = new Set(
    Array.from(idp.getElementsByTagNameNS(SAML_MD_NS, 'KeyDescriptor'))
      .filter((key) => [null, 'signing'].includes(key.getAttribute('use')))
      .flatMap((key) => Array.from(key.getElementsByTagNameNS(XML_DSIG_NS, 'X509Certificate')))
      .map((cert) => cert.textContent?.replace(/\s+/g, ''))
      .filter((text): text is string => !!text)
  );
  if (!certificates.size) return { error: SAML_METADATA_NO_CERTIFICATE_MSG };
  // During a key rollover metadata lists both keys, and we can't tell which is active.
  if (certificates.size > 1) return { error: SAML_METADATA_MULTIPLE_CERTIFICATES_MSG };
  const [certificate] = Array.from(certificates);

  return { values: { samlIssuerId, loginUrl, certificate } };
};
